import { useCallback, useMemo } from "react";
import { useQuery } from "@tanstack/react-query";
import { get } from "lodash";

import { queryClient } from "App";
import { getCampaignTableColumns } from "routes/entity/components/campaign_table/CampaignTableColumnsAndFilters";
import { CAMPAIGN_TABLE_CONSTANTS } from "routes/entity/components/campaign_table/CampaignTableV3";
import { campaignViewKeys } from "routes/entity/components/campaign_table/queryKeys";
import CampaignService from "services/Campaign";
import UserService from "services/User";
import { addResizedColumns } from "./useTableConfig";

export const useCampaignViewTable = ({
  canEmail,
  sendEmail,
  entityId,
  contactId,
}) => {
  const { data: tableSettings, isSuccess: isTableSettingsFetched } = useQuery({
    queryKey: ["table-settings"],
    queryFn: UserService.getUserSettings,
  });

  const queryKey = useMemo(
    () => campaignViewKeys.list(entityId ? { entityId } : { contactId }),
    [entityId, contactId]
  );

  const invalidateData = useCallback(async () => {
    await queryClient.invalidateQueries({ queryKey });
  }, [queryKey]);

  const handleRemoveCampaignView = useCallback(
    async ({ campaign_id, entity_id, contact_id }) => {
      if (contact_id) {
        await CampaignService.removeContact(campaign_id, entity_id, contact_id);
      } else {
        await CampaignService.deleteCampaignTarget(campaign_id, entity_id);
      }
      await invalidateData();
    },
    [invalidateData]
  );

  const columns = useMemo(() => {
    if (!isTableSettingsFetched) return [];
    const initialColumns = getCampaignTableColumns({
      canEmail,
      sendEmail,
      entityId,
      invalidateData,
      handleRemoveCampaignView,
    });
    const campaignViewTableSettings = get(
      tableSettings,
      `user_settings.preferences.tables.${CAMPAIGN_TABLE_CONSTANTS.tableName}`,
      {}
    );
    const withResizedColumns = addResizedColumns({
      columns: initialColumns,
      resizedColumns: campaignViewTableSettings?.resized_columns || {},
    });
    return withResizedColumns;
  }, [
    isTableSettingsFetched,
    canEmail,
    sendEmail,
    entityId,
    tableSettings,
    invalidateData,
    handleRemoveCampaignView,
  ]);

  return { columns, queryKey, invalidateData };
};
